import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as partnerApi from '../../api/partnerApi';
import { downloadBulkTemplate, exportPartners, importPartners } from '../../api/partnerApi';
import { formatDateOnly } from '../../utils/formatDate';

/**
 * Status filter tabs for the partner list
 */
const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending_review', label: 'Pending Review' },
];

/**
 * Human-readable labels for party types
 */
const PARTY_TYPE_LABELS = {
  VENDOR: 'Vendor',
  BUYER: 'Buyer',
  CONSIGNEE: 'Consignee',
  SHIPPER: 'Shipper',
  BOOKING_AGENT: 'Booking Agent',
  NOTIFY_PARTY: 'Notify Party',
  CARRIER: 'Carrier',
  BENEFICIAL_CARGO_OWNER: 'Beneficial Cargo Owner',
  ENDORSEE: 'Endorsee',
  ALSO_NOTIFY_PARTY: 'Also Notify',
  FREIGHT_PAYER: 'Freight Payer',
};

/**
 * PartnerList Page
 *
 * Main Partners management page for admins.
 * Shows a filterable list of partners. Click navigates to detail page.
 */
function PartnerList() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importDryRun, setImportDryRun] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const menuRef = useRef(null);

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const result = await partnerApi.getPartners(params);
      setPartners(result.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownloadTemplate = async (format) => {
    setMenuOpen(false);
    try {
      if (format === 'json') {
        // API returns { success, data } for JSON — data is the template array
        const result = await downloadBulkTemplate('json');
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, 'partner-template.json');
      } else {
        // API returns the Blob directly for xlsx (interceptor unwraps response.data)
        const blob = await downloadBulkTemplate('xlsx');
        downloadBlob(blob, 'partner-template.xlsx');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download template');
    }
  };

  const handleExport = async (format) => {
    setMenuOpen(false);
    try {
      if (format === 'json') {
        const result = await exportPartners('json');
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        downloadBlob(blob, 'partners-export.json');
      } else {
        const blob = await exportPartners('xlsx');
        downloadBlob(blob, 'partners-export.xlsx');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export partners');
    }
  };

  const handleImport = async (isDryRun) => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      // API returns { success, data } — interceptor unwraps response.data
      const result = await importPartners(importFile, isDryRun);
      setImportResult(result.data); // result is { success, data: { summary, results, dryRun } }
      if (!isDryRun) fetchPartners();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
      if (err.response?.data?.details) {
        setImportResult({
          summary: { created: 0, updated: 0, deleted: 0, failed: err.response.data.details.length, total: err.response.data.details.length },
          results: err.response.data.details.map(d => ({ ...d, status: 'failed', error: d.error })),
          dryRun: isDryRun,
        });
      }
    } finally {
      setImportLoading(false);
    }
  };

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Collect unique party types from addresses and subdivision addresses
   */
  const getPartyTypes = (partner) => {
    const types = new Set();
    // Collect from top-level addresses
    for (const addr of (partner.addresses || [])) {
      for (const pt of (addr.partyTypes || [])) types.add(pt);
    }
    // Collect from subdivision addresses
    for (const sub of (partner.subdivisions || [])) {
      for (const addr of (sub.addresses || [])) {
        for (const pt of (addr.partyTypes || [])) types.add(pt);
      }
    }
    return [...types];
  };

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
          {!loading && (
            <span className="bg-gray-100 text-gray-600 text-sm font-medium px-2.5 py-0.5 rounded-full">
              {partners.length}
            </span>
          )}
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="More actions"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">Download Template</div>
              <button onClick={() => handleDownloadTemplate('xlsx')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">XLSX</button>
              <button onClick={() => handleDownloadTemplate('json')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">JSON</button>
              <div className="border-t border-gray-100 my-1" />
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">Export Partners</div>
              <button onClick={() => handleExport('xlsx')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">XLSX</button>
              <button onClick={() => handleExport('json')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">JSON</button>
              <div className="border-t border-gray-100 my-1" />
              <button onClick={() => { setMenuOpen(false); setImportModalOpen(true); setImportResult(null); setImportFile(null); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Import Partners
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`
                whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm
                ${statusFilter === tab.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {!loading && partners.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No partners found</p>
        </div>
      )}

      {!loading && partners.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aliases</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subdivisions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party Types</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {partners.map((partner) => (
                <tr
                  key={partner._id}
                  onClick={() => navigate(`/admin/partners/${partner._id}`)}
                  className={`
                    cursor-pointer hover:bg-gray-50 transition-colors duration-150
                    ${partner.status === 'pending_review' ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-transparent'}
                  `}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {partner.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex flex-wrap gap-1">
                      {(partner.aliases || []).map((alias, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {alias}
                        </span>
                      ))}
                      {(!partner.aliases || partner.aliases.length === 0) && (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {partner.subdivisions?.length > 0
                      ? <span className="text-xs text-gray-600">{partner.subdivisions.length} division{partner.subdivisions.length !== 1 ? 's' : ''}</span>
                      : <span className="text-gray-400 text-xs">—</span>
                    }
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex flex-wrap gap-1">
                      {getPartyTypes(partner).map((type) => (
                        <span key={type} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                          {PARTY_TYPE_LABELS[type] || type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      partner.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {partner.status === 'active' ? 'Active' : 'Pending Review'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateOnly(partner.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {importModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Import Partners</h2>
              <button onClick={() => setImportModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1">
              {/* File drop zone */}
              {!importResult && (
                <>
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors">
                    <div className="text-center">
                      <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                      <p className="text-sm text-gray-500">
                        {importFile ? importFile.name : 'Drop .xlsx or .json file here, or click to browse'}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".xlsx,.json"
                      onChange={(e) => setImportFile(e.target.files[0] || null)}
                    />
                  </label>

                  <div className="flex items-center gap-4 mt-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={importDryRun}
                        onChange={(e) => setImportDryRun(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Dry run (preview only)
                    </label>
                  </div>
                </>
              )}

              {/* Results */}
              {importResult && (
                <>
                  <div className="flex gap-3 mb-4 flex-wrap">
                    {importResult.dryRun && (
                      <span className="px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">DRY RUN</span>
                    )}
                    <span className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">Created: {importResult.summary.created}</span>
                    <span className="px-2.5 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Updated: {importResult.summary.updated}</span>
                    <span className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Deleted: {importResult.summary.deleted}</span>
                    {importResult.summary.failed > 0 && (
                      <span className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Failed: {importResult.summary.failed}</span>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Row</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {importResult.results.map((r, i) => (
                          <tr key={i} className={r.status === 'failed' ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2 text-gray-600">{r.row ?? r.index ?? i}</td>
                            <td className="px-3 py-2 text-gray-900">{r.action}</td>
                            <td className="px-3 py-2 text-gray-600">{r.type || '\u2014'}</td>
                            <td className="px-3 py-2 text-gray-900">{r.name || '\u2014'}</td>
                            <td className="px-3 py-2">
                              <span className={`text-xs font-medium ${r.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>{r.status}</span>
                            </td>
                            <td className="px-3 py-2 text-red-600 text-xs">{r.error || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              {!importResult && (
                <button
                  onClick={() => handleImport(importDryRun)}
                  disabled={!importFile || importLoading}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importLoading ? 'Processing...' : importDryRun ? 'Preview' : 'Import'}
                </button>
              )}
              {importResult && importResult.dryRun && (
                <button
                  onClick={() => handleImport(false)}
                  disabled={importLoading}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {importLoading ? 'Processing...' : 'Confirm & Execute'}
                </button>
              )}
              {importResult && !importResult.dryRun && (
                <button
                  onClick={() => setImportModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerList;
